import { fragmentAddress } from "@saleor/fragments/address";
import {
  exportErrorFragment,
  invoiceErrorFragment,
  orderErrorFragment
} from "@saleor/fragments/errors";
import {
  exportFileFragment,
  fragmentOrderDetails,
  fragmentOrderEvent,
  invoiceFragment
} from "@saleor/fragments/orders";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import { TypedMutation } from "../mutations";
import { AssignRider, AssignRiderVariables } from "./types/AssignRider";
import { Book2go, Book2goVariables } from "./types/Book2go";
import {
  BookThirdPartyRider,
  BookThirdPartyRiderVariables
} from "./types/BookThirdPartyRider";
import {
  CancelAutoBooking,
  CancelAutoBookingVariables
} from "./types/CancelAutobooking";
import { FulfillOrder, FulfillOrderVariables } from "./types/FulfillOrder";
import {
  InvoiceEmailSend,
  InvoiceEmailSendVariables
} from "./types/InvoiceEmailSend";
import {
  InvoiceRequest,
  InvoiceRequestVariables
} from "./types/InvoiceRequest";
import {
  OpsMarkAsValidParcel,
  OpsMarkAsValidParcelVariables
} from "./types/OpsMarkAsValidParcel";
import { OrderAddNote, OrderAddNoteVariables } from "./types/OrderAddNote";
import { OrderCancel, OrderCancelVariables } from "./types/OrderCancel";
import {
  OrderCancelGrabExpress,
  OrderCancelGrabExpressVariables
} from "./types/OrderCancelGrabExpress";
import {
  OrderCancelPandagoAuto,
  OrderCancelPandagoAutoVariables
} from "./types/OrderCancelPandagoAuto";
import { OrderCapture, OrderCaptureVariables } from "./types/OrderCapture";
import {
  OrderDraftBulkCancel,
  OrderDraftBulkCancelVariables
} from "./types/OrderDraftBulkCancel";
import {
  OrderDraftCancel,
  OrderDraftCancelVariables
} from "./types/OrderDraftCancel";
import { OrderDraftCreate } from "./types/OrderDraftCreate";
import {
  OrderDraftFinalize,
  OrderDraftFinalizeVariables
} from "./types/OrderDraftFinalize";
import {
  OrderDraftUpdate,
  OrderDraftUpdateVariables
} from "./types/OrderDraftUpdate";
import { OrderExport, OrderExportVariables } from "./types/OrderExport";
import {
  OrderFulfillmentCancel,
  OrderFulfillmentCancelVariables
} from "./types/OrderFulfillmentCancel";
import {
  orderFulfillmentRemarksMutation,
  orderFulfillmentRemarksMutationVariables
} from "./types/orderFulfillmentRemarksMutation";
import {
  OrderFulfillmentUpdateTracking,
  OrderFulfillmentUpdateTrackingVariables
} from "./types/OrderFulfillmentUpdateTracking";
import {
  OrderLineDelete,
  OrderLineDeleteVariables
} from "./types/OrderLineDelete";
import { OrderLinesAdd, OrderLinesAddVariables } from "./types/OrderLinesAdd";
import {
  OrderLineUpdate,
  OrderLineUpdateVariables
} from "./types/OrderLineUpdate";
import {
  orderMarkAsCompleted,
  orderMarkAsCompletedVariables
} from "./types/orderMarkAsCompleted";
import {
  orderMarkAsPacked,
  orderMarkAsPackedVariables
} from "./types/orderMarkAsPacked";
import {
  OrderMarkAsPaid,
  OrderMarkAsPaidVariables
} from "./types/OrderMarkAsPaid";
import {
  orderMarkAsReadyToShip,
  orderMarkAsReadyToShipVariables
} from "./types/orderMarkAsReadyToShip";
import {
  orderMarkAsShipped,
  orderMarkAsShippedVariables
} from "./types/orderMarkAsShipped";
import { OrderRefund, OrderRefundVariables } from "./types/OrderRefund";
import {
  orderReturnToFc,
  orderReturnToFcVariables
} from "./types/orderReturnToFc";
import {
  OrderShippingMethodUpdate,
  OrderShippingMethodUpdateVariables
} from "./types/OrderShippingMethodUpdate";
import { OrderUpdate, OrderUpdateVariables } from "./types/OrderUpdate";
import {
  orderUpdateDropOffLocationMutation,
  orderUpdateDropOffLocationMutationVariables
} from "./types/orderUpdateDropOffLocationMutation";
import { OrderVoid, OrderVoidVariables } from "./types/OrderVoid";
import {
  RefreshGrabExpressStatus,
  RefreshGrabExpressStatusVariables
} from "./types/RefreshGrabExpressStatus";
import {
  RefreshPandagoAutoStatus,
  RefreshPandagoAutoStatusVariables
} from "./types/RefreshPandagoAutoStatus";
import {
  StoreOrderExport,
  StoreOrderExportVariables
} from "./types/StoreOrderExport";
import {
  updateOrderFulfillmentRemarksMutation,
  updateOrderFulfillmentRemarksMutationVariables
} from "./types/updateOrderFulfillmentRemarksMutation";
import {
  UpdateOrderWithOverride,
  UpdateOrderWithOverrideVariables
} from "./types/UpdateOrderWithOverride";
import {
  UpdateShippingLabel,
  UpdateShippingLabelVariables
} from "./types/UpdateShippingLabel";

const orderCancelMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderCancel($id: ID!, $cancelReason: String!) {
    orderCancel(id: $id, cancelReason: $cancelReason) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderCancelMutation = TypedMutation<
  OrderCancel,
  OrderCancelVariables
>(orderCancelMutation);

export const useOrderCancelMutation = makeMutation<
  OrderCancel,
  OrderCancelVariables
>(orderCancelMutation);

const orderMarkAsPackedMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation orderMarkAsPacked($id: ID!) {
    orderMarkAsPacked(id: $id) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const useMarkAsPackedMutation = makeMutation<
  orderMarkAsPacked,
  orderMarkAsPackedVariables
>(orderMarkAsPackedMutation);

const orderMarkAsReadyToShipMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation orderMarkAsReadyToShip($id: ID!) {
    orderMarkAsReadyToShip(id: $id) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const useMarkAsReadyToShipMutation = makeMutation<
  orderMarkAsReadyToShip,
  orderMarkAsReadyToShipVariables
>(orderMarkAsReadyToShipMutation);

const orderMarkAsShippedMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation orderMarkAsShipped($id: ID!) {
    orderMarkAsShipped(id: $id) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;

export const useMarkAsShippedMutation = makeMutation<
  orderMarkAsShipped,
  orderMarkAsShippedVariables
>(orderMarkAsShippedMutation);

const orderReturnToFcMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation orderReturnToFc($id: ID!) {
    orderReturnToFc(id: $id) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;

// export const orderReturnToFcMutation = makeMutation<>(orderReturnToFc);

export const useOrderReturnToFcMutation = makeMutation<
  orderReturnToFc,
  orderReturnToFcVariables
>(orderReturnToFcMutation);

const orderMarkAsCompletedMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation orderMarkAsCompleted(
    $id: ID!
    $otherReason: String
    $reason: CompletedReasonsEnum!
  ) {
    orderMarkAsCompleted(id: $id, otherReason: $otherReason, reason: $reason) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const useMarkAsCompletedMutation = makeMutation<
  orderMarkAsCompleted,
  orderMarkAsCompletedVariables
>(orderMarkAsCompletedMutation);

const orderDraftCancelMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderDraftCancel($id: ID!) {
    draftOrderDelete(id: $id) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderDraftCancelMutation = TypedMutation<
  OrderDraftCancel,
  OrderDraftCancelVariables
>(orderDraftCancelMutation);

const orderDraftBulkCancelMutation = gql`
  ${orderErrorFragment}
  mutation OrderDraftBulkCancel($ids: [ID]!) {
    draftOrderBulkDelete(ids: $ids) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
    }
  }
`;
export const TypedOrderDraftBulkCancelMutation = TypedMutation<
  OrderDraftBulkCancel,
  OrderDraftBulkCancelVariables
>(orderDraftBulkCancelMutation);

const orderDraftFinalizeMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderDraftFinalize($id: ID!) {
    draftOrderComplete(id: $id) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderDraftFinalizeMutation = TypedMutation<
  OrderDraftFinalize,
  OrderDraftFinalizeVariables
>(orderDraftFinalizeMutation);

const orderRefundMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderRefund($id: ID!, $amount: PositiveDecimal!) {
    orderRefund(id: $id, amount: $amount) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderRefundMutation = TypedMutation<
  OrderRefund,
  OrderRefundVariables
>(orderRefundMutation);

const orderVoidMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderVoid($id: ID!) {
    orderVoid(id: $id) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderVoidMutation = TypedMutation<
  OrderVoid,
  OrderVoidVariables
>(orderVoidMutation);

const orderMarkAsPaidMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderMarkAsPaid($id: ID!) {
    orderMarkAsPaid(id: $id) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderMarkAsPaidMutation = TypedMutation<
  OrderMarkAsPaid,
  OrderMarkAsPaidVariables
>(orderMarkAsPaidMutation);

const orderCaptureMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderCapture($id: ID!, $amount: PositiveDecimal!) {
    orderCapture(id: $id, amount: $amount) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderCaptureMutation = TypedMutation<
  OrderCapture,
  OrderCaptureVariables
>(orderCaptureMutation);

const orderFulfillmentUpdateTrackingMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderFulfillmentUpdateTracking(
    $id: ID!
    $input: FulfillmentUpdateTrackingInput!
  ) {
    orderFulfillmentUpdateTracking(id: $id, input: $input) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderFulfillmentUpdateTrackingMutation = TypedMutation<
  OrderFulfillmentUpdateTracking,
  OrderFulfillmentUpdateTrackingVariables
>(orderFulfillmentUpdateTrackingMutation);

const orderFulfillmentCancelMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderFulfillmentCancel($id: ID!, $input: FulfillmentCancelInput!) {
    orderFulfillmentCancel(id: $id, input: $input) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderFulfillmentCancelMutation = TypedMutation<
  OrderFulfillmentCancel,
  OrderFulfillmentCancelVariables
>(orderFulfillmentCancelMutation);

const orderAddNoteMutation = gql`
  ${fragmentOrderEvent}
  ${orderErrorFragment}
  mutation OrderAddNote($order: ID!, $input: OrderAddNoteInput!) {
    orderAddNote(order: $order, input: $input) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        id
        events {
          ...OrderEventFragment
        }
      }
    }
  }
`;
export const TypedOrderAddNoteMutation = TypedMutation<
  OrderAddNote,
  OrderAddNoteVariables
>(orderAddNoteMutation);

const orderUpdateMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderUpdate($id: ID!, $input: OrderUpdateInput!) {
    orderUpdate(id: $id, input: $input) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderUpdateMutation = TypedMutation<
  OrderUpdate,
  OrderUpdateVariables
>(orderUpdateMutation);

const orderDraftUpdateMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderDraftUpdate($id: ID!, $input: DraftOrderInput!) {
    draftOrderUpdate(id: $id, input: $input) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderDraftUpdateMutation = TypedMutation<
  OrderDraftUpdate,
  OrderDraftUpdateVariables
>(orderDraftUpdateMutation);

const orderShippingMethodUpdateMutation = gql`
  ${orderErrorFragment}
  mutation OrderShippingMethodUpdate(
    $id: ID!
    $input: OrderUpdateShippingInput!
  ) {
    orderUpdateShipping(order: $id, input: $input) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        availableShippingMethods {
          id
          name
        }
        id
        shippingMethod {
          id
          name
          price {
            amount
            currency
          }
        }
        shippingMethodName
        shippingPrice {
          gross {
            amount
            currency
          }
        }
      }
    }
  }
`;
export const TypedOrderShippingMethodUpdateMutation = TypedMutation<
  OrderShippingMethodUpdate,
  OrderShippingMethodUpdateVariables
>(orderShippingMethodUpdateMutation);

const orderDraftCreateMutation = gql`
  ${orderErrorFragment}
  mutation OrderDraftCreate {
    draftOrderCreate(input: {}) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        id
      }
    }
  }
`;
export const useOrderDraftCreateMutation = makeMutation<
  OrderDraftCreate,
  unknown
>(orderDraftCreateMutation);

const orderLineDeleteMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderLineDelete($id: ID!) {
    draftOrderLineDelete(id: $id) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderLineDeleteMutation = TypedMutation<
  OrderLineDelete,
  OrderLineDeleteVariables
>(orderLineDeleteMutation);

const orderLinesAddMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderLinesAdd($id: ID!, $input: [OrderLineCreateInput]!) {
    draftOrderLinesCreate(id: $id, input: $input) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderLinesAddMutation = TypedMutation<
  OrderLinesAdd,
  OrderLinesAddVariables
>(orderLinesAddMutation);

const orderLineUpdateMutation = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation OrderLineUpdate($id: ID!, $input: OrderLineInput!) {
    draftOrderLineUpdate(id: $id, input: $input) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const TypedOrderLineUpdateMutation = TypedMutation<
  OrderLineUpdate,
  OrderLineUpdateVariables
>(orderLineUpdateMutation);

const fulfillOrder = gql`
  ${fragmentOrderDetails}
  ${orderErrorFragment}
  mutation FulfillOrder($orderId: ID!, $input: OrderFulfillInput!) {
    orderFulfill(order: $orderId, input: $input) {
      errors: orderErrors {
        ...OrderErrorFragment
        warehouse
        orderLine
      }
      order {
        ...OrderDetailsFragment
      }
    }
  }
`;
export const useOrderFulfill = makeMutation<
  FulfillOrder,
  FulfillOrderVariables
>(fulfillOrder);

const invoiceRequestMutation = gql`
  ${invoiceErrorFragment}
  ${invoiceFragment}
  mutation InvoiceRequest($orderId: ID!) {
    invoiceRequest(orderId: $orderId) {
      errors: invoiceErrors {
        ...InvoiceErrorFragment
      }
      invoice {
        ...InvoiceFragment
      }
      order {
        id
        invoices {
          ...InvoiceFragment
        }
      }
    }
  }
`;
export const TypedInvoiceRequestMutation = TypedMutation<
  InvoiceRequest,
  InvoiceRequestVariables
>(invoiceRequestMutation);

const invoiceEmailSendMutation = gql`
  ${invoiceErrorFragment}
  ${invoiceFragment}
  mutation InvoiceEmailSend($id: ID!) {
    invoiceSendEmail(id: $id) {
      errors: invoiceErrors {
        ...InvoiceErrorFragment
      }
      invoice {
        ...InvoiceFragment
      }
    }
  }
`;
export const TypedInvoiceEmailSendMutation = TypedMutation<
  InvoiceEmailSend,
  InvoiceEmailSendVariables
>(invoiceEmailSendMutation);

const storeOrderMarkAsValidForPacking = gql`
  mutation OpsMarkAsValidParcel($id: ID!, $parcelNo: String!) {
    storeOrderMarkAsValidForPacking(id: $id, parcelNo: $parcelNo) {
      storeOrder {
        id
        orderCode
        parcelNo
      }
      event {
        id
        date
        type
        user {
          firstName
        }
        message
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useOrderMarkAsValid = makeMutation<
  OpsMarkAsValidParcel,
  OpsMarkAsValidParcelVariables
>(storeOrderMarkAsValidForPacking);

const orderAssignRider = gql`
  mutation AssignRider(
    $id: ID!
    $riderId: ID
    $isRider3pl: Boolean
    $rider3plContactNo: String
    $rider3plFirstName: String
    $rider3plLastName: String
    $rider3plTrackingUrl: String
    $courier: String
    $timeDelivered: DateTime
    $timeDispatched: DateTime
    $lateReason: String
    $lateDispatchReason: String
  ) {
    orderAssignRider(
      id: $id
      riderId: $riderId
      isRider3pl: $isRider3pl
      rider3plContactNo: $rider3plContactNo
      rider3plFirstName: $rider3plFirstName
      rider3plLastName: $rider3plLastName
      rider3plTrackingUrl: $rider3plTrackingUrl
      courier: $courier
      timeDelivered: $timeDelivered
      timeDispatched: $timeDispatched
      lateReason: $lateReason
      lateDispatchReason: $lateDispatchReason
    ) {
      order {
        orderCode
        thirdPartyRider {
          firstName
          lastName
          contactNo
          deliveryCourier {
            order {
              courier
            }
            homeLocation {
              id
              name
            }
            senderFirstName
            senderLastName
            senderContactNo
            shippingCompany
            referenceNo
            status
          }
        }
      }
      rider {
        id
        firstName
        lastName
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useOrderAssignRider = makeMutation<
  AssignRider,
  AssignRiderVariables
>(orderAssignRider);

const orderUpdateShippingLabel = gql`
  ${fragmentAddress}
  mutation UpdateShippingLabel(
    $id: ID!
    $awbNo: String!
    $dispatchNo: String!
    $specialNotes: String!
    $courier: String!
  ) {
    orderUpdateShippingLabel(
      id: $id
      awbNo: $awbNo
      dispatchNo: $dispatchNo
      specialNotes: $specialNotes
      courier: $courier
    ) {
      order {
        id
        awbNo
        courier
        dispatchNo
        specialNotes
        billingAddress {
          ...AddressFragment
        }
        smopStatus
        created
        customerNote
        number
        payments {
          id
          paymentMethodType
          chargeStatus
          created
        }
        paymentStatus
        shippingAddress {
          ...AddressFragment
          latitude
          longitude
          unitDetails
          streetBrgy
          landmark
          provinceName
        }
        shippingSchedule
        shippingMethod {
          deliveryType
          id
        }
        shippingMethodName
        shippingPrice {
          gross {
            amount
            currency
          }
        }
        status
        subtotal {
          gross {
            amount
            currency
          }
        }
        mall {
          id
          name
          address {
            id
            streetAddress1
            streetAddress2
            cityArea
            city
            countryArea
            postalCode
          }
          warehouses(first: 100) {
            edges {
              node {
                id
                name
                address {
                  id
                  companyName
                  streetAddress1
                  streetAddress2
                  city
                  cityArea
                  postalCode
                  country {
                    country
                  }
                  countryArea
                  phone
                }
              }
            }
          }
        }
        orderCode
        subOrders {
          id
          orderCode
          smopStatus
          parcelNo
          validatedAt
          validatedBy {
            id
            firstName
            lastName
            email
          }
          validatedForPackingBy {
            id
            email
            firstName
          }
          tenant {
            id
            legalName
            storeName
            brand {
              id
              name
              logo {
                url
                alt
              }
            }
          }
          lines {
            id
            productName
            variantName
            quantity
            unitPrice {
              gross {
                amount
              }
            }
            storeOrder {
              id
            }
            totalPrice {
              gross {
                amount
              }
            }
            status
            notes
          }
          discountAmount
          totalAmount
          subtotal
        }
        total {
          gross {
            amount
            currency
          }
          tax {
            amount
            currency
          }
        }
        totalAuthorized {
          amount
          currency
        }
        totalCaptured {
          amount
          currency
        }
        user {
          id
          email
          firstName
          lastName
          contactNo
          defaultShippingAddress {
            id
            streetAddress1
            streetAddress2
            city
            cityArea
            postalCode
            country {
              country
            }
            countryArea
            phone
          }
        }
        userEmail
        availableShippingMethods {
          id
          name
          price {
            amount
            currency
          }
        }
        discountName
        discount {
          amount
          currency
        }
        rider {
          id
          email
          firstName
          lastName
          contactNo
        }
        cancelReason
        isMultiTenant
        verticalType
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useUpdateShippingLabel = makeMutation<
  UpdateShippingLabel,
  UpdateShippingLabelVariables
>(orderUpdateShippingLabel);

const updateMotherOrderStatusWithOverride = gql`
  mutation UpdateOrderWithOverride(
    $id: ID!
    $overrideReason: String
    $smopStatus: SmopOrderStatusEnum!
  ) {
    updateOrderStatus(
      id: $id
      overrideReason: $overrideReason
      smopStatus: $smopStatus
    ) {
      order {
        id
        orderCode
        smopStatus
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useUpdateOrderStatusWithOverride = makeMutation<
  UpdateOrderWithOverride,
  UpdateOrderWithOverrideVariables
>(updateMotherOrderStatusWithOverride);

export const orderExportMutation = gql`
  ${exportFileFragment}
  ${exportErrorFragment}
  mutation OrderExport($input: ExportOrdersInput!) {
    exportOrders(input: $input) {
      exportFile {
        ...ExportFileFragment
      }
      errors: exportErrors {
        ...ExportErrorFragment
      }
    }
  }
`;

export const useOrderExport = makeMutation<OrderExport, OrderExportVariables>(
  orderExportMutation
);
export const orderCancelGrabExpressMutation = gql`
  ${fragmentOrderDetails}
  mutation OrderCancelGrabExpress($id: ID!) {
    orderCancelGrabExpress(id: $id) {
      order {
        ...OrderDetailsFragment
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useOrderCancelGrabExpress = makeMutation<
  OrderCancelGrabExpress,
  OrderCancelGrabExpressVariables
>(orderCancelGrabExpressMutation);

export const orderCancelPandagoAutoMutation = gql`
  mutation OrderCancelPandagoAuto($id: ID!, $cancelReason: String!) {
    orderCancelPandagoAuto(orderId: $id, cancelReason: $cancelReason) {
      order {
        id
        thirdPartyRider {
          deliveryCourier {
            cancelReason
          }
        }
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;
export const useOrderCancelPandagoAuto = makeMutation<
  OrderCancelPandagoAuto,
  OrderCancelPandagoAutoVariables
>(orderCancelPandagoAutoMutation);

export const refreshGrabExpressStatus = gql`
  mutation RefreshGrabExpressStatus($id: ID!) {
    refreshGrabExpressStatus(id: $id) {
      order {
        id
        subOrders {
          tenant {
            brand {
              name
            }
          }
        }
        created
        orderCode
        shippingMethod {
          deliveryType
        }
        thirdPartyRider {
          deliveryCourier {
            status
            homeLocation {
              name
            }
            senderLastName
            senderFirstName
            senderContactNo
            referenceNo
            notes
            trackingUrl
          }
          firstName
          lastName
          contactNo
        }
        payments {
          paymentMethodType
        }
        total {
          gross {
            amount
            currency
          }
        }
        user {
          firstName
          lastName
          contactNo
        }
        shippingAddress {
          streetAddress1
          streetAddress2
          unitDetails
          streetBrgy
          landmark
          city
          latitude
          longitude
        }
        aging
        dwellTime
        totalTime
        courier
        number
        paymentStatus
        status
        userEmail
        cancelReason
        isMultiTenant
        shippedAt
        completedAt
        onTime
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useRefreshGrabExpressStatus = makeMutation<
  RefreshGrabExpressStatus,
  RefreshGrabExpressStatusVariables
>(refreshGrabExpressStatus);

export const refreshPandaGoAutoStatus = gql`
  mutation RefreshPandagoAutoStatus($id: ID!) {
    refreshPandagoAutoStatus(id: $id) {
      order {
        thirdPartyRider {
          deliveryCourier {
            status
          }
        }
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useRefreshPandaGoAutoStatus = makeMutation<
  RefreshPandagoAutoStatus,
  RefreshPandagoAutoStatusVariables
>(refreshPandaGoAutoStatus);

export const bookThirdPartyRider = gql`
  mutation BookThirdPartyRider(
    $id: ID!
    $courier: String!
    $dropOffLocationId: ID
    $lateDispatchReason: String
    $lateReason: String
    $contactPersonContactNo: String!
    $contactPersonFirstName: String!
    $contactPersonLastName: String!
  ) {
    bookThirdPartyRider(
      id: $id
      courier: $courier
      dropOffLocationId: $dropOffLocationId
      lateDispatchReason: $lateDispatchReason
      lateReason: $lateReason
      contactPersonContactNo: $contactPersonContactNo
      contactPersonFirstName: $contactPersonFirstName
      contactPersonLastName: $contactPersonLastName
    ) {
      order {
        id
        parcelNo
        smopStatus
        verticalType
        orderCode
        rider {
          id
          firstName
          lastName
        }
        isRider3pl
        created
        status
        shippedAt
        completedAt
        thirdPartyRider {
          id
          deliveryCourier {
            order {
              courier
            }
            homeLocation {
              id
              name
            }
            senderLastName
            senderFirstName
            senderContactNo
            shippingCompany
            referenceNo
          }
        }
        lastRiderAssignedAt
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useBookThirdPartyRider = makeMutation<
  BookThirdPartyRider,
  BookThirdPartyRiderVariables
>(bookThirdPartyRider);

export const storeOrderExportMutation = gql`
  ${exportFileFragment}
  ${exportErrorFragment}
  mutation StoreOrderExport($input: ExportStoreOrdersInput!) {
    exportStoreOrders(input: $input) {
      exportFile {
        ...ExportFileFragment
      }
      errors: exportErrors {
        ...ExportErrorFragment
      }
    }
  }
`;

export const useStoreOrderExport = makeMutation<
  StoreOrderExport,
  StoreOrderExportVariables
>(storeOrderExportMutation);

const orderUpdateDropOffLocation = gql`
  mutation orderUpdateDropOffLocationMutation($dropOffLocation: ID!, $id: ID!) {
    orderUpdateDropOffLocation(dropOffLocation: $dropOffLocation, id: $id) {
      order {
        id
        orderCode
        smopStatus
        dropOffLocation {
          id
          name
          code
          status
        }
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useOrderAssignHomeLocation = makeMutation<
  orderUpdateDropOffLocationMutation,
  orderUpdateDropOffLocationMutationVariables
>(orderUpdateDropOffLocation);

const updateOrderFulfillmentRemarks = gql`
  mutation updateOrderFulfillmentRemarksMutation($id: ID!, $remarks: String!) {
    updateOrderFulfillmentRemarks(id: $id, remarks: $remarks) {
      order {
        id
        orderCode
        orderFulfillmentRemarks {
          message
        }
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const useUpdateOrderFulfillmentRemarks = makeMutation<
  updateOrderFulfillmentRemarksMutation,
  updateOrderFulfillmentRemarksMutationVariables
>(updateOrderFulfillmentRemarks);

const orderFulfillmentRemarks = gql`
  mutation orderFulfillmentRemarksMutation($id: ID!, $remarks: String!) {
    orderFulfillmentRemarks(id: $id, remarks: $remarks) {
      order {
        id
        orderCode
        orderFulfillmentRemarks {
          message
        }
      }
      orderErrors {
        field
        message
        code
      }
    }
  }
`;

export const userOrderFulfillmentRemarks = makeMutation<
  orderFulfillmentRemarksMutation,
  orderFulfillmentRemarksMutationVariables
>(orderFulfillmentRemarks);

const book2go = gql`
  mutation Book2go($id: ID!, $input: Book2goInput!) {
    book2go(id: $id, input: $input) {
      order {
        id
        thirdPartyAwbCode
        orderCode
        courier
        deliveryCourier {
          status
          failedReason
          shippingCompany
        }
      }
      orderErrors {
        field
        message
      }
    }
  }
`;

export const useBook2Go = makeMutation<Book2go, Book2goVariables>(book2go);

const cancelAutoBooking = gql`
  mutation CancelAutoBooking($orderId: ID!) {
    cancelAutoBooking(orderId: $orderId) {
      autoBookingError {
        field
        message
        code
      }
      order {
        id
        orderCode
        bookingTime
        bookingTaskId
        bookingCourier
        deliveryCourier {
          status
        }
      }
    }
  }
`;

export const useCancelAutoBooking = makeMutation<
  CancelAutoBooking,
  CancelAutoBookingVariables
>(cancelAutoBooking);
